// app/views/components/shared/CalendarEventsSection.tsx
import { useState } from 'react'
import { router } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { formatDate } from '@/lib/utils'
import { Calendar, CheckCircle2, Plus, Trash2, ChevronDown, ChevronRight } from 'lucide-react'

type CalendarEvent = {
  id: string
  title: string
  due_date: string
  source: string
  completed: boolean
  complete_path?: string
  delete_path?: string
}

type CalendarEventsSectionProps = {
  events: CalendarEvent[]
  createPath?: string
  collapsible?: boolean
}

export function CalendarEventsSection({ events, createPath, collapsible = true }: CalendarEventsSectionProps) {
  const [open, setOpen] = useState(!collapsible || events.length > 0)
  const [showForm, setShowForm] = useState(false)
  const [title, setTitle] = useState('')
  const [dueDate, setDueDate] = useState('')

  const handleCreate = () => {
    if (!createPath || !title || !dueDate) return
    router.post(createPath, { title, due_date: dueDate, description: '' }, {
      onSuccess: () => { setShowForm(false); setTitle(''); setDueDate('') }
    })
  }

  return (
    <section className="border rounded-lg">
      <div
        className={`px-4 py-3 flex items-center justify-between ${collapsible ? 'cursor-pointer' : ''} ${open ? 'border-b' : ''}`}
        onClick={collapsible ? () => setOpen(!open) : undefined}
      >
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-muted-foreground" />
          <h2 className="text-sm font-medium">Calendar Events</h2>
          {events.length > 0 && (
            <span className="text-xs text-muted-foreground">({events.length})</span>
          )}
        </div>
        {collapsible && (open ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />)}
      </div>
      {open && (
        <div className="px-4 py-3 space-y-2">
          {events.length === 0 && !showForm && (
            <p className="text-sm text-muted-foreground">No upcoming events</p>
          )}
          {events.map(event => (
            <div key={event.id} className="flex items-center justify-between py-1.5">
              <div className="flex items-center gap-2 min-w-0">
                {event.completed ? (
                  <CheckCircle2 className="w-3.5 h-3.5 text-green-600 shrink-0" />
                ) : (
                  <Calendar className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                )}
                <span className={`text-sm truncate ${event.completed ? 'line-through text-muted-foreground' : ''}`}>{event.title}</span>
              </div>
              <div className="flex items-center gap-2 shrink-0 ml-2">
                <span className="text-xs text-muted-foreground">{formatDate(event.due_date)}</span>
                {!event.completed && event.complete_path && (
                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => router.post(event.complete_path!)}>
                    <CheckCircle2 className="w-3.5 h-3.5" />
                  </Button>
                )}
                {event.delete_path && (
                  <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => router.delete(event.delete_path!)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            </div>
          ))}
          {showForm && (
            <div className="flex items-end gap-2 pt-2">
              <Input placeholder="Event title" value={title} onChange={e => setTitle(e.target.value)} className="h-8 text-sm flex-1" />
              <Input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} className="h-8 text-sm w-36" />
              <Button size="sm" className="h-8 text-xs" onClick={handleCreate}>Add</Button>
              <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          )}
          {createPath && !showForm && (
            <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={() => setShowForm(true)}>
              <Plus className="w-3 h-3" />Add Event
            </Button>
          )}
        </div>
      )}
    </section>
  )
}
